import { createContext, useContext, type ReactNode, type CSSProperties } from 'react'

/**
 * The DOM node that Radix portals (Modal, DropdownMenu, the command palette…)
 * mount into.
 *
 * Why this exists: white-label tenant theming is applied by writing the brand's
 * CSS custom properties as INLINE styles on the app-shell element (see
 * `applyBrandVars` + AppLayout). Radix portals default to `document.body`,
 * which sits OUTSIDE that element, so a portaled dialog/menu would render in the
 * default EventRipple palette instead of the tenant's brand. Pointing the
 * portals at the brand-scoped shell node makes those custom properties cascade
 * into the portaled content.
 *
 * `null` (the default, when no provider is mounted — e.g. public/auth layouts)
 * means "fall back to `document.body`", i.e. Radix's own default.
 *
 * Floating chrome mounted ABOVE this provider (InstallPrompt, UpdateBanner)
 * cannot read the context and uses `./brand-shell` instead, which carries the
 * same node. That lives in its own module because this file exports components,
 * and mixing component and non-component exports breaks Fast Refresh
 * (`react-refresh/only-export-components`).
 */
const PortalContainerContext = createContext<HTMLElement | null>(null)

export const PortalContainerProvider = PortalContainerContext.Provider

/** The brand-scoped portal container, or `null` to use Radix's default (body). */
export function usePortalContainer(): HTMLElement | null {
  return useContext(PortalContainerContext)
}

// Menu depth follows React ownership, even when the DOM is portaled to the
// brand shell. A child dialog must cover its parent's open menu, while its own
// menu must clear its backdrop. Root menus retain the ordinary dropdown layer.
const OverlayDepthContext = createContext(0)
export function ModalLayerProvider({ children }: { children: ReactNode }) {
  const depth = useContext(OverlayDepthContext)
  return <OverlayDepthContext.Provider value={depth + 1}>{children}</OverlayDepthContext.Provider>
}
export function useOverlayLayer(kind: 'modal' | 'menu'): CSSProperties {
  const depth = useContext(OverlayDepthContext)
  return { zIndex: kind === 'menu' && depth === 0 ? 'var(--z-dropdown)' : `calc(var(--z-modal) + ${kind === 'modal' ? depth * 2 : depth * 2 - 1})` }
}
